import { apiHosts } from "@/api/constants/apiHosts";
import { LE } from "@/constants/enums";
import type { RequestValidationError } from "@/helpers/requestValidationError";
import type { Inquiry, InquiryNew } from "@/views/inquiry/inquiry";
import { NotifyOptionsError, NotifyOptionsSuccess } from "@modernary/mui/components/notifications";
import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import type { AxiosResponse } from "axios";
import InquiryService from "./inquiryService";

const URL = "inquiries";
const ENTITY_NAME = "Inquiry";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_IHEER_API_BASE_URL,
};

const api = apiService<Inquiry, Paging<Inquiry>>(apiHosts.IHEER, axiosParams);
const canDeleteApi = apiService<RequestValidationError>(apiHosts.IHEER, axiosParams);

/**
 * @param {Object} params Query parameters, varies (TODO improve this)
 * @param {abortCallback} config.abortMethod Callback for when a request has been aborted
 */
const getInquiries = (params: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getInquiryById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewInquiry = async () => {
  //optionally make any other changes as necessary before returning.
  return { data: InquiryService.getInitialData() } as AxiosResponse<InquiryNew, any>;
};

const createInquiry = (Inquiry: Inquiry, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, Inquiry, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateInquiry = (Inquiry: Inquiry, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + Inquiry.id, Inquiry, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Inquiry} Inquiry This cannot be a 'ref' to a Inquiry
 */
const saveInquiry = (Inquiry: Inquiry, config: PagingApiAndNotifierConfig = {}) => {
  if (Inquiry.id) {
    return updateInquiry(Inquiry, config);
  } else {
    return createInquiry(Inquiry, config);
  }
};

const deleteInquiryById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const canDeleteInquiry = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return canDeleteApi.delete(URL + "/" + id + "?" + "validateOnly=" + true, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  canDeleteInquiry, createInquiry, deleteInquiryById, getInquiries, getInquiryById, getNewInquiry, saveInquiry, updateInquiry
};

